import { useCallback, useEffect, useRef, useState } from 'react';
import { wireGraph } from './useGraphWiring';
import { useEngineRefs } from './refs';
// Element transport removed; all playback via buffer/worklet path
import { useSyncController } from './useSyncController';
import { useAnalyserConfig } from './useAnalyserConfig';
import { useMetersEnvelope } from './useMetersEnvelope';
import { useVolumesSoloMute } from './useVolumesSoloMute';
import type { UseAudioMixEngine, UseAudioMixEngineArgs, StemLevels } from './types';
import { scheduleStartAll, stopAll, type ScheduledStem, disposeScheduled, flushAllCaches, onSwapCacheMaintenance } from '../bufferTransport';
import { resetRbWorkletState, ensureRubberBandWorklet, detachRubberBandWorklets } from '../bufferTransport/worklets';
import { resetRbWasmCache } from '../rbLoader';
import { useSeekPipeline } from './useSeekPipeline';
import { useSpeedDsp } from './useSpeedDsp';
import { makeTransportGate } from './helpers/transportGate';
import { openGateWhenSignal } from './helpers/health';
import { ensureDestinationAlive } from '../graphNodes';
import { useWireAndLoad } from './useWireAndLoad';
import { useTimeTicker } from './useTimeTicker';
import { L } from '../../../utils/logger';
import { setPreservesPitchForAll } from '../../timeStretch';
import { useElementDurations } from './useElementDurations';
import { useTransportControls } from './useTransportControls';
import { MIN_DB, meterFromDb, gainToDb } from '../../../lib/audioMath';
import { useEqParams } from './parts/useEqParams';
import { useWaveformParams } from './parts/useWaveformParams';
import { useMasterAndFftParams } from './parts/useMasterAndFftParams';
import { useLevelsState } from './parts/useLevelsState';
import { useAnalyserTap } from './parts/useAnalyserTap';
import { useScheduledGainSync } from './parts/useScheduledGainSync';
import { useGlobalSafety } from './parts/useGlobalSafety';
import coldStart from './coldStart';
import { userGestureSeen, resumeCtxOnGesture } from './autoplayGate';
// Seek helpers moved to helpers/seekHelpers
import type { TransportClock } from '../../../scheduler/clock';
import { computeSpeedMapping } from '../speedMapping';
import { getCtx, ctxOk } from '../ctxSingleton';

export default function useMixEngineOrchestrator({ tracks, masterFftOrder: initialFftOrder, bufferMode }: UseAudioMixEngineArgs): UseAudioMixEngine {
  const refs = useEngineRefs();

  // Core state
  const [status, setStatus] = useState<'idle' | 'loading' | 'playing' | 'paused'>('idle');
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  // Master & FFT
  const {
    masterVolume, setMasterVolume,
    masterPlaybackRate, setMasterPlaybackRate,
    masterFftOrder, setMasterFftOrder,
    masterFftSmoothing, setMasterFftSmoothing,
  } = useMasterAndFftParams(initialFftOrder);
  // EQ params
  const {
    eqEnabled, setEqEnabled,
    eqHpfEnabled, setEqHpfEnabled,
    eqLpfEnabled, setEqLpfEnabled,
    eqQ, setQ,
    masterLowpassHz, setMasterLowpassHz,
    masterHighpassHz, setMasterHighpassHz,
  } = useEqParams(refs);
  const [timeStretchEnabled, setTimeStretchEnabled] = useState(true);
  // Default: maintain pitch (user asked default is speed does not affect pitch)
  const [maintainPitch, setMaintainPitch] = useState(true);
  const [freqResponseEnabled, setFreqResponseEnabled] = useState(true);
  const [masterEnvelopeEnabled, setMasterEnvelopeEnabled] = useState(false);
  // Surface per-stem load errors to UI
  const [stemErrors, setStemErrors] = useState<Record<string, { status?: number; message: string; url?: string }> | null>(null);
  // Waveform DSP params
  const { waveformWindowMs, setWaveformWindowMs, waveformFps, setWaveformFps, waveformGrid, setWaveformGrid, waveformHoriz, setWaveformHoriz, waveformVert, setWaveformVert } = useWaveformParams();
  // Background behavior: if true, we keep playing when tab/window loses focus (default ON)
  const [keepPlayingInBackground] = useState(true);

  // Volumes / mute / solo
  const { volumes, muted, solo, setVolume, toggleMute, toggleSolo, getEffectiveGain } = useVolumesSoloMute();

  // Buffer-mode state
  const bufferModeRef = useRef<boolean>(!!bufferMode);
  const buffersRef = useRef<Record<string, AudioBuffer> | null>(null);
  const buffersSigRef = useRef<string>('');
  const loadRunIdRef = useRef(0);
  // Version token that increments on every scope change to cancel stale async work
  const scopeVersionRef = useRef(0);
  const resumeAfterLoadRef = useRef(false);
  const scheduledRef = useRef<Record<string, ScheduledStem> | null>(null);
  const scheduledMetaRef = useRef<{ version: number; sig: string } | null>(null);
  const logicalStartRef = useRef<number>(0); // engine logical start time (seconds)
  const startedAtRef = useRef<number>(0);    // ctx.currentTime when scheduled
  // Throttle guard to avoid back-to-back scheduleStartAll calls
  const nextSchedAtRef = useRef<number>(0);
  // Milestone 2: simple clock ref for diagnostics (not wired yet)
  const transportClockRef = useRef<TransportClock | null>(null);
  // Track when a foreground reschedule happened to avoid back-to-back reschedules
  const visRescheduleAtRef = useRef<number>(0);
  // Explicit user pause guard to prevent unintended auto-resume
  const userPausedRef = useRef(false);
  // Track an active seek session to pin UI and cool drift controller
  const seekingRef = useRef<{ runId: number; target: number } | null>(null);
  // Guard to ensure we only fire natural auto-stop once per play cycle
  const naturalStopRef = useRef(false);
  // Persist user pitch scale (Rubber Band pitch_scale) across reschedules
  const rbPitchRef = useRef<number>(1.0);
  // Persist pitch in semitones for UI and engine-level reapply
  const pitchStRef = useRef<number>(0);
  try { (refs as any).pitchStRef = pitchStRef; } catch {}
  // Once-per-scope soft reset guard
  const lastResetForScopeRef = useRef<number>(-1);

  // Transport (element path removed). Provide minimal pauseAll stub.
  const pauseAll = () => { /* no element path */ };
  const audioRefFor = () => () => {}; // API compatibility no-op

  // useAudioRefs no longer needed; element graph disabled.

  // audioRefFor caching is encapsulated in useAudioRefs

  // Wiring and loading handled by useWireAndLoad

  // Wiring and buffer loading
  useWireAndLoad({
    tracks,
    refs,
    masterVolume,
    masterFftOrder,
    masterHighpassHz,
    masterLowpassHz,
    eqQ,
    eqEnabled,
    eqHpfEnabled,
    eqLpfEnabled,
    freqResponseEnabled,
    getEffectiveGain,
    bufferModeRef,
    buffersRef,
    buffersSigRef,
    loadRunIdRef,
  scopeVersionRef,
    resumeAfterLoadRef,
    scheduledRef,
  scheduledMetaRef,
    logicalStartRef,
    startedAtRef,
    status,
    masterPlaybackRate,
  maintainPitch,
    pauseAll,
    setDuration,
  setStatus,
  userPausedRef,
  setStemErrors,
  });
  // Analyser config
  useAnalyserConfig(refs.masterAnalyserRef.current, masterFftOrder, masterFftSmoothing);

  // Levels & envelope (freeze on pause, clear on idle)
  const meters = useMetersEnvelope(status, refs.analyzersRef, getEffectiveGain, masterEnvelopeEnabled, refs.masterAnalyserRef);
  const { levels, setLevels, lastLevelsRef } = useLevelsState(status, meters);
  useEffect(() => {
    if (status === 'paused') {
      // Freeze meters at last running snapshot
      setLevels(lastLevelsRef.current);
    } else if (status === 'idle') {
      // On Stop: force all stems to -160 dBFS floor, preserving attenuation labels
      const floorLevels: Record<string, StemLevels> = {};
      for (let i = 0; i < tracks.length; i++) {
        const role = tracks[i].role;
        const srcDb = MIN_DB;
        const preMeter = meterFromDb(srcDb);
        const g = getEffectiveGain(role);
        const attDb = gainToDb(g);
        const outDb = g === 0 ? srcDb : (isFinite(attDb) ? srcDb + attDb : undefined);
        const postMeter = typeof outDb === 'number' ? meterFromDb(outDb) : 0;
        floorLevels[role] = { preMeter, srcDb, postMeter, outDb, attenuationDb: attDb } as StemLevels;
      }
      setLevels(floorLevels);
      lastLevelsRef.current = floorLevels;
    }
  }, [status, tracks, getEffectiveGain]);

  // Element-mode duration tracking handled by hook
  useElementDurations(bufferModeRef.current, tracks, refs, setDuration);
  // Current-time ticker handled by hook
  useTimeTicker({
    tracks,
    refs,
    status,
    masterPlaybackRate,
    duration,
    bufferModeRef,
    logicalStartRef,
    startedAtRef,
    seekingRef,
    setCurrentTime,
  });

  // Ensure element path (if ever used) has preservesPitch set once
  const didSetPreservesPitchRef = useRef(false);
  useEffect(() => {
    if (didSetPreservesPitchRef.current) return;
    try { setPreservesPitchForAll(refs.elsRef.current, tracks as any, true); } catch {}
    didSetPreservesPitchRef.current = true;
  }, [tracks]);

  // Sync controller (live drift) — disabled in buffer mode
  const lastSeekAtRef = useRef(0);
  useSyncController(!bufferModeRef.current && timeStretchEnabled && status === 'playing', tracks, refs.elsRef, masterPlaybackRate, lastSeekAtRef as any);

  // Build a small transport gate to mute/unmute master during seeks (memoized)
  const gateFnsRef = useRef<ReturnType<typeof makeTransportGate> | null>(null);
  if (!gateFnsRef.current) gateFnsRef.current = makeTransportGate(refs);
  const { closeTransportGate, openTransportGate } = gateFnsRef.current;

  // Ensure the master analyser is tapped on the audible path when toggling maintain-pitch or EQ flags
  useAnalyserTap(refs, [eqEnabled, eqHpfEnabled, eqLpfEnabled, maintainPitch, freqResponseEnabled, masterEnvelopeEnabled]);

  // Transport wrappers to keep status consistent
  const play = useCallback(async () => {
    if (!refs.audioCtxRef.current || !ctxOk(refs.audioCtxRef.current)) {
      refs.audioCtxRef.current = getCtx();
    }
    const ctx = refs.audioCtxRef.current!;
    // Clear user-paused guard on explicit Play
    userPausedRef.current = false;
  // Reset natural stop latch at the beginning of a new play cycle
  naturalStopRef.current = false;
    try {
      if (ctx.state === 'suspended') {
        if (userGestureSeen()) { await ctx.resume().catch(() => {}); }
        else { void resumeCtxOnGesture(ctx); }
      }
    } catch {}
    // Clear any stop-installed kill-switch handlers before we attempt to play elements
    try {
      const els = refs.elsRef.current as Record<string, HTMLAudioElement | null>;
      for (const role of Object.keys(els)) {
        const el = els[role]; if (!el) continue;
        try { (el as any).onplay = null; } catch {}
        try { (el as any).onseeking = null; } catch {}
      }
    } catch {}
    const needWire = !refs.masterGainRef.current;
    if (needWire) {
      wireGraph(
        ctx,
        tracks,
        refs,
        { masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, q: eqQ },
  { eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled: (freqResponseEnabled || masterEnvelopeEnabled) },
        getEffectiveGain,
      );
      // Proactively ensure destination hop is alive
      try { ensureDestinationAlive(ctx, { masterGainRef: refs.masterGainRef, masterHighpassRef: refs.masterHighpassRef, masterFilterRef: refs.masterFilterRef }); } catch {}
    }
  // Helper: re-anchor transport clock using previous anchor if present
  // Policy: call only on play/seek or when rate actually changes; do NOT call per frame.
    const reanchorClock = (actx: AudioContext) => {
      const clk = (refs as any).transportClockRef?.current as TransportClock | null | undefined;
      const mediaNow = clk
        ? clk.t0Media + (actx.currentTime - clk.t0Audio) * clk.rate
        : logicalStartRef.current;
      (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
      (refs as any).transportClockRef.current = {
        t0Audio: actx.currentTime,
        t0Media: mediaNow,
        rate: masterPlaybackRate,
      } as TransportClock;
  try { L.info('media:transport', 'clock.anchor', (refs as any).transportClockRef.current); } catch {}
    };

  // Helper: throttle + reschedule with clock reanchor (at most once per small window)
    const reanchorClockAndReschedule = () => {
      const actx = refs.audioCtxRef.current as AudioContext | null;
      const sched = scheduledRef.current;
      if (!actx || !sched) return;
      const hidden = (() => { try { return typeof document !== 'undefined' && document.visibilityState !== 'visible'; } catch { return false; } })();
      // Use larger lookahead in background to tolerate timer throttling
      const whenLA = hidden ? 0.25 : 0.08;
      reanchorClock(actx);
      const earliest = actx.currentTime + whenLA;
      if (actx.currentTime < nextSchedAtRef.current - 0.003) return;
      nextSchedAtRef.current = earliest + 0.02;
      // Close transport gate before any (re)schedule to avoid blasting partial graphs
      try { closeTransportGate(); } catch {}
      const when = scheduleStartAll(
        actx,
        sched,
        whenLA,
        logicalStartRef.current,
        masterPlaybackRate,
        undefined,
        {
          seamlessSwap: true,
          // Add a bit more fixed alignment latency when hidden
          fixedLatencySec: maintainPitch ? (hidden ? 0.08 : 0.04) : 0,
          clock: (refs as any).transportClockRef?.current,
        }
      );
      // After scheduling, ensure RB tempo matches stored UI tempo (don’t overwrite to 1.0)
      try {
        if (maintainPitch) {
          const tempo = (refs as any).rbTempoRef?.current;
          if (tempo != null) {
            for (const role of Object.keys(sched)) {
              try { sched[role]?.worklet?.node?.port?.postMessage({ type: 'tempo', value: tempo }); } catch {}
            }
          }
        } else {
          // Not maintain-pitch: neutralize RB tempo
          for (const role of Object.keys(sched)) {
            try { sched[role]?.worklet?.node?.port?.postMessage({ type: 'tempo', value: 1.0 }); } catch {}
          }
          (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
          (refs as any).rbTempoRef.current = 1.0;
        }
      } catch {}
      startedAtRef.current = when;
      // Health probe loop: open when signal appears (handles late-arrival audio)
      try { openGateWhenSignal(refs); } catch {}
      return when;
    };
    // Expose helper for other orchestrator hooks
    (refs as any).reanchorClockAndReschedule = reanchorClockAndReschedule;
    // Force re-tap master analyser explicitly on Play (without touching LPF->destination)
    try {
      const { retapMasterAnalyser } = await import('../graphNodes');
      retapMasterAnalyser(refs as any, { eqEnabled, eqHpfEnabled, eqLpfEnabled });
    } catch {}
  if (bufferModeRef.current) {
      // Only allow play if the scheduled graph matches the current scope version and signature
      const currentSig = tracks.map(t => `${t.role}|${(t as any).url ?? ''}`).join('||');
      const okMeta = scheduledMetaRef.current &&
        scheduledMetaRef.current.version === scopeVersionRef.current &&
        scheduledMetaRef.current.sig === currentSig;
      if (buffersRef.current && scheduledRef.current && okMeta) {
  // Re-anchor + schedule (throttled)
  try { reanchorClockAndReschedule(); } catch {}
        setStatus('playing');
        try { openTransportGate(60); } catch {}
      } else {
        resumeAfterLoadRef.current = true;
        setStatus('loading');
      }
    }
  }, [tracks, masterVolume, masterFftOrder, masterHighpassHz, masterLowpassHz, eqQ, eqEnabled, eqHpfEnabled, eqLpfEnabled, freqResponseEnabled, masterEnvelopeEnabled, getEffectiveGain]);

  const { pause, stop } = useTransportControls({
    refs,
    bufferModeRef,
    scheduledRef,
    logicalStartRef,
    startedAtRef,
    masterPlaybackRate,
    pauseAll,
    setStatus,
    setCurrentTime,
    resumeAfterLoadRef,
    seekingRef,
    userPausedRef,
  });

  // Wrap stop to also reset visual meters/envelope
  const stopAndReset = useCallback(() => {
    try { stop(); } catch {}
    try { closeTransportGate(); } catch {}
    try { meters.reset?.(); } catch {}
  // Keep last levels visible on Stop; UI shows frozen values
  }, [stop, meters]);

  // Auto-stop when playback naturally reaches the end (parity with explicit Stop)
  useEffect(() => {
    if (status !== 'playing') return;
    if (!duration || duration === Infinity) return;
    // currentTime is clamped to duration by ticker; small epsilon to ensure we catch it
    if (currentTime >= duration - 0.005 && !naturalStopRef.current) {
      naturalStopRef.current = true; // latch
      // Use same pathway as manual Stop for consistent meter + state reset
      try { stopAndReset(); } catch {}
      // Race guard: a final rAF tick from the play loop may clamp UI time to "end".
      // Force-reset the slider back to 0:00 after state flips to idle so UX matches expectations.
      try { setTimeout(() => { try { setCurrentTime(0); } catch {} }, 0); } catch {}
    }
  }, [status, currentTime, duration, stopAndReset]);

  // Keep scheduled gains in sync with UI volumes in buffer mode
  useScheduledGainSync(bufferModeRef, scheduledRef, getEffectiveGain, [volumes, muted, solo, getEffectiveGain]);

  // Centralized speed DSP (rate + pitch)
  useSpeedDsp({
    refs,
    tracks,
    bufferModeRef,
    scheduledRef,
    logicalStartRef,
    startedAtRef,
    masterPlaybackRate,
    status,
    maintainPitch,
  pauseAll,
  scheduleStartAll,
  setStatus,
  getEffectiveGain,
  resumeAfterLoadRef,
  userPausedRef,
  });

  // If flipping maintain-pitch while playing, ensure transport gate is open
  useEffect(() => {
    if (status === 'playing') {
      try { openTransportGate(60); } catch {}
    }
  }, [maintainPitch, status]);

  // Keep the clock's rate synced with UI speed (media speed), independent of WA playbackRate
  useEffect(() => {
    try {
      if (transportClockRef.current) {
        transportClockRef.current.rate = masterPlaybackRate;
      }
      // Also mirror on refs if present (seek path creates it there)
      if ((refs as any).transportClockRef?.current) {
        (refs as any).transportClockRef.current.rate = masterPlaybackRate;
      }
    } catch {}
  }, [masterPlaybackRate]);

  // React to AudioContext state changes and tab visibility changes: recompute offsets and reschedule
  useEffect(() => {
    const actx = refs.audioCtxRef.current as AudioContext | null;
    const handleState = () => {
      try {
        if (status !== 'playing' || !bufferModeRef.current || !scheduledRef.current || !actx) return;
        // Recompute media offset from current audio time and restart schedule with a small lead
        const base = Math.max(0, (actx.currentTime - (startedAtRef.current || 0))) + (logicalStartRef.current || 0);
        const hidden = (() => { try { return typeof document !== 'undefined' && document.visibilityState !== 'visible'; } catch { return false; } })();
        // If we just performed a visibility-driven reschedule very recently, skip this statechange one
        try {
          const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
          if (!hidden && now - (visRescheduleAtRef.current || 0) < 350) return;
        } catch {}
        const whenLA = hidden ? 0.25 : 0.05;
        // Close gate before reschedule to prevent clicks
        try { makeTransportGate(refs).closeTransportGate(); } catch {}
        const when = scheduleStartAll(
          actx,
          scheduledRef.current,
          whenLA,
          base,
          masterPlaybackRate,
          undefined,
          { seamlessSwap: true, fixedLatencySec: maintainPitch ? (hidden ? 0.08 : 0.04) : 0 }
        );
        if (typeof when === 'number') startedAtRef.current = when;
        // Open gate when signal present
        try { openGateWhenSignal(refs); } catch {}
      } catch {}
    };
    const handleVis = () => {
      try {
        const hidden = document.visibilityState !== 'visible';
        if (!hidden) {
          // Returning to foreground: try to resume context
          try {
            if (actx && actx.state === 'suspended') {
              if (userGestureSeen()) { void actx.resume().catch(() => {}); }
              else { void resumeCtxOnGesture(actx); }
            }
          } catch {}
          // If we're playing and have scheduled stems, perform a safe reschedule with gate protection
          if (status === 'playing' && bufferModeRef.current && scheduledRef.current && actx) {
            try {
              const { closeTransportGate, openTransportGate } = makeTransportGate(refs);
              // Close gate to avoid partial/overlap clicks during reschedule
              try { closeTransportGate(); } catch {}
              // Re-anchor the transport clock to maintain continuous media time
              try {
                const clk: any = (refs as any).transportClockRef?.current ?? null;
                const rate = Number.isFinite((refs as any).transportClockRef?.current?.rate) ? (refs as any).transportClockRef.current.rate : (typeof masterPlaybackRate === 'number' ? masterPlaybackRate : 1.0);
                const mediaNow = clk ? (clk.t0Media + (actx.currentTime - clk.t0Audio) * clk.rate) : (logicalStartRef.current || 0);
                (refs as any).transportClockRef = (refs as any).transportClockRef || { current: null };
                (refs as any).transportClockRef.current = { t0Audio: actx.currentTime, t0Media: mediaNow, rate } as any;
              } catch {}
              // Compute base offset and reschedule with slightly larger foreground lead
              const base = Math.max(0, (actx.currentTime - (startedAtRef.current || 0))) + (logicalStartRef.current || 0);
              const whenLA = 0.12;
              const when = scheduleStartAll(
                actx,
                scheduledRef.current,
                whenLA,
                base,
                masterPlaybackRate,
                undefined,
                { seamlessSwap: true, fixedLatencySec: maintainPitch ? 0.06 : 0, clock: (refs as any).transportClockRef?.current }
              );
              if (typeof when === 'number') startedAtRef.current = when;
              try { visRescheduleAtRef.current = (typeof performance !== 'undefined' ? performance.now() : Date.now()); } catch {}
              // Open the gate when signal is actually present to avoid a pop on return
              try { openGateWhenSignal(refs, { thresholdDb: -85, timeoutMs: 4000, intervalMs: 120, rampMs: 80 }); } catch { try { openTransportGate(80); } catch {} }
            } catch {}
          }
        }
        // When going hidden, avoid forcing an immediate reschedule to prevent audible glitches.
      } catch {}
    };
    try { actx?.addEventListener('statechange', handleState as any); } catch {}
    try { document.addEventListener('visibilitychange', handleVis); } catch {}
    return () => {
      try { actx?.removeEventListener('statechange', handleState as any); } catch {}
      try { document.removeEventListener('visibilitychange', handleVis); } catch {}
    };
  }, [status, bufferModeRef, scheduledRef, refs, masterPlaybackRate, maintainPitch, logicalStartRef, startedAtRef, initialFftOrder, setMasterFftOrder]);

  // Fixed alignment latency for pitch-preserving path
  // When Maintain pitch is ON, apply a small uniform delay to all stems to align with look-ahead.
  // When OFF, remove the extra latency.
  useEffect(() => {
    const LAT_MS = 42; // tune 30–60ms if needed
    const ctx = refs.audioCtxRef.current as AudioContext | null;
    const delaySeconds = maintainPitch ? LAT_MS / 1000 : 0;
    try {
      const dict = refs.delaysRef.current as Record<string, DelayNode>;
      for (const role of Object.keys(dict || {})) {
        const d = dict[role];
        if (!d) continue;
        try { d.delayTime.setValueAtTime(delaySeconds, ctx?.currentTime ?? 0); }
        catch { try { (d.delayTime as any).value = delaySeconds; } catch {} }
      }
    } catch {}
    // Attach or detach Rubber Band processors based on maintainPitch flag
    try {
      const stems = (refs as any).scheduledRef?.current as Record<string, any> | null;
      const ctxNow = refs.audioCtxRef.current as AudioContext | null;
      if (stems && ctxNow) {
        if (maintainPitch) {
          // Only create nodes if missing; modules guarded to load once
          void ensureRubberBandWorklet(ctxNow, stems).catch(() => {});
        } else {
          // Remove processors to keep graph minimal when not needed
          detachRubberBandWorklets(stems);
        }
      }
    } catch {}
  }, [maintainPitch, tracks]);

  // Replace large inline seek with dedicated pipeline
  const { seek } = useSeekPipeline({
    tracks,
    refs,
    bufferModeRef,
    scheduledRef,
    logicalStartRef,
    startedAtRef,
    masterPlaybackRate,
  maintainPitch,
    status,
    pauseAll,
    scheduleStartAll,
    stopAll,
    setStatus,
    setCurrentTime,
    lastSeekAtRef,
    seekingRef,
    openTransportGate,
    closeTransportGate,
    duration,
  resumeAfterLoadRef,
  userPausedRef,
  });

  const onScopeChange = useCallback(() => {
    // Increment version to invalidate any in-flight async loads/starts
    scopeVersionRef.current += 1;
    // Preserve user's Vinyl Mode (maintainPitch) setting across scope swaps
    // Leave rbTempoRef unchanged here; mapping will be re-applied during scheduling
    // Runtime/build flags: prefer soft cold start and cache flush per swap (default: soft/no hard reset)
    const hardCold = (() => {
      try { const v = (process as any)?.env?.REACT_APP_MEDIA_HARD_COLD_START; if (v != null) return v === '1' || String(v).toLowerCase() === 'true'; } catch {}
      try { const w = (window as any); if (w.__MEDIA_HARD_COLD_START === true) return true; const v2 = localStorage.getItem('MEDIA_HARD_COLD_START'); if (v2 != null) return v2 === '1' || String(v2).toLowerCase() === 'true'; } catch {}
      return false; // default: keep one AudioContext / Worklet VM (no hard reset per swap)
    })();
    // Scheduled hard rotation: after N swaps, force a hard reset regardless of the flag
    const HARD_RESET_EVERY = (() => {
      // Default: disabled. Only rotate if explicitly set to a positive integer
      const parseNum = (s: any) => { const n = parseInt(String(s), 10); return isFinite(n) ? n : NaN; };
      try {
        const v = (process as any)?.env?.REACT_APP_MEDIA_HARD_RESET_EVERY;
        if (v != null) { const n = parseNum(v); if (!isNaN(n)) return Math.max(0, n); }
      } catch {}
      try {
        const v2 = localStorage.getItem('MEDIA_HARD_RESET_EVERY');
        if (v2 != null) { const n = parseNum(v2); if (!isNaN(n) && n > 0) return n; }
      } catch {}
      return 0; // default: disabled cadence
    })();
    try { (refs as any)._swapCount = ((refs as any)._swapCount || 0) + 1; } catch {}
    const doHardCycle = (() => {
      try {
        const n = (refs as any)._swapCount | 0;
        if (!HARD_RESET_EVERY || HARD_RESET_EVERY <= 0) return false; // disabled
        return (n > 0) && ((n % HARD_RESET_EVERY) === 0);
      } catch { return false; }
    })();
    const doHard = hardCold || doHardCycle;
    const flushCaches = (() => {
      try { const v = (process as any)?.env?.REACT_APP_MEDIA_FLUSH_CACHES; if (v != null) return v === '1' || String(v).toLowerCase() === 'true'; } catch {}
      try { const w = (window as any); if (w.__MEDIA_FLUSH_CACHES === true) return true; const v2 = localStorage.getItem('MEDIA_FLUSH_CACHES'); if (v2 != null) return v2 === '1' || String(v2).toLowerCase() === 'true'; } catch {}
      return true; // default: flush caches each swap
    })();
    // Proactively drop cache references so GC can reclaim memory sooner
    // If we won't auto-resume (fresh play), flush; otherwise strictly enforce budgets
    try { onSwapCacheMaintenance(!!resumeAfterLoadRef.current && !doHard); } catch {}
    if (flushCaches) { try { flushAllCaches(); } catch {} try { resetRbWorkletState(); } catch {} try { resetRbWasmCache(); } catch {} }
    // True cold start (soft): dump graphs, worklets, buffers and reset UI
    if ((refs as any)._scopeColdStartRunId === scopeVersionRef.current) return;
    (refs as any)._scopeColdStartRunId = scopeVersionRef.current;
    void coldStart(refs, {
      hard: doHard,
      bufferModeRef,
      scheduledRef,
      disposeScheduled,
      stopAll,
      pauseAll,
      setStatus,
      setCurrentTime,
    });
  // Ensure we don't keep cache items around after teardown
  if (flushCaches) { try { flushAllCaches(); } catch {} try { resetRbWorkletState(); } catch {} try { resetRbWasmCache(); } catch {} }
    // On scheduled hard rotations, flush caches and reset WASM cache even if flushCaches flag is off
    if (doHardCycle) { try { flushAllCaches(); } catch {} try { resetRbWasmCache(); } catch {} }
    // Ensure we don't accidentally auto-resume with stale buffers
    try { resumeAfterLoadRef.current = false; } catch {}
    // Force loading state until new buffers are wired
    setStatus(prev => (prev === 'loading' ? prev : 'loading'));
  }, [stop, setCurrentTime, setStatus]);

  // Lightweight on-demand triage function: call in dev console as window.__mediaTriage?.()
  try {
    (window as any).__mediaTriage = () => {
      const ctx: AudioContext | null = refs.audioCtxRef.current;
      const sched = scheduledRef.current || {};
      const roles = Object.keys(sched);
      const started = roles.map(r => ({
        role: r,
        startedAt: (sched as any)[r]?.source?._startTime ?? null,
        offsetAtStart: (sched as any)[r]?.source?._offset ?? null,
      }));
      // Gate/master snapshot
      const gate = Number(refs.gateGainRef.current?.gain?.value ?? 0);
      const master = Number(refs.masterGainRef.current?.gain?.value ?? 0);
      console.table({
        status,
        ctxState: ctx?.state,
        ctxTime: ctx?.currentTime,
        roles: roles.length,
        gate,
        master,
      } as any);
      console.table(started);
      return { status, ctxState: ctx?.state, ctxTime: ctx?.currentTime, roles, gate, master, started };
    };
  } catch {}

  // Param setters with clamping
  const setLowpass = useCallback((hz: number) => { setMasterLowpassHz(hz); }, [setMasterLowpassHz]);
  const setHighpass = useCallback((hz: number) => { setMasterHighpassHz(hz); }, [setMasterHighpassHz]);

  // Apply EQ param updates when enabled
  // Q updates handled inside useEqParams

  // Diagnostics
  const getSyncErrors = useCallback(() => ({} as Record<string, number>), []);

  useGlobalSafety(refs, {
    keepPlayingInBackground,
    bufferModeRef,
    scheduledRef,
    pauseAll,
    stopAll,
    setStatus,
    setCurrentTime,
  });

  // --- Rubber Band controls (tempo & pitch) ---
  // Debounce UI tempo posts to avoid flooding worklet; dispatch after a short delay
  const tempoDebounceRef = useRef<{ timers: Record<string, any>; values: Record<string, number> }>({ timers: {}, values: {} });
  const setRbTempo = useCallback((roleOrAll: string | '*', value: number) => {
    // Persist last requested tempo so we can reapply on new scopes/worklet nodes
    try {
      (refs as any).rbTempoRef = (refs as any).rbTempoRef || { current: null };
      (refs as any).rbTempoRef.current = value;
  // Also persist globally so new worklet nodes (initRbNode) can pick it up immediately
  try { (globalThis as any).__rbLastTempo = value; } catch {}
    } catch {}
    const key = String(roleOrAll);
    const DEBOUNCE_MS = 80;
    const state = tempoDebounceRef.current;
    state.values[key] = value;
    try { clearTimeout(state.timers[key]); } catch {}
    state.timers[key] = setTimeout(() => {
      const sched = scheduledRef.current;
      if (!sched) return;
      const v = state.values[key];
      const apply = (role: string) => {
        const s = sched[role];
        if (!s) return;
        // Prefer wrapper API if available
        if (s.worklet?.setTempo) {
          try { s.worklet.setTempo(v); } catch {}
          return;
        }
        // Fallback: post an object payload understood by the worklet
        if (s.worklet?.node?.port) {
          try { s.worklet.node.port.postMessage({ type: 'tempo', value: v }); } catch {}
        }
      };
      if (roleOrAll === '*') {
        for (const r of Object.keys(sched)) apply(r);
      } else {
        apply(roleOrAll as string);
      }
    }, DEBOUNCE_MS);
  }, []);

  const setRbPitch = useCallback((roleOrAll: string | '*', value: number) => {
    const v = Math.max(0.01, value || 1);
    try { rbPitchRef.current = v; } catch {}
    try { (window as any).__rbLastPitch = v; } catch {}
    const sched = scheduledRef.current;
    if (!sched) return;
    const rate = masterPlaybackRate;
    const maintain = maintainPitch;
    // If we have mapping context, combine user pitch with mapping for fast regime
    const map = typeof rate === 'number' ? computeSpeedMapping(rate, !!maintain) : null as any;
    const apply = (role: string) => {
      const s = sched[role];
      if (!s) return;
      let out = v;
      if (maintain && map) {
        if (rate > 1.0 + 1e-3) out = (map.rbPitch || 1.0) * v;
        else out = v; // slow/stretch regime uses user pitch only
      }
      // Prefer wrapper API if available
      if (s.worklet?.setPitch) {
        try { s.worklet.setPitch(out); } catch {}
        return;
      }
      // Fallback: post an object payload understood by the worklet
      if (s.worklet?.node?.port) {
        try { s.worklet.node.port.postMessage({ type: 'pitch', value: out }); } catch {}
      }
    };
    if (roleOrAll === '*') {
      for (const r of Object.keys(sched)) apply(r);
    } else {
      apply(roleOrAll);
    }
  }, []);

  // Engine-level semitone pitch API
  const setPitchSemitones = useCallback((st: number) => {
    const clamped = Math.max(-12, Math.min(12, st));
    pitchStRef.current = clamped;
    const scale = Math.pow(2, clamped / 12);
    // keep legacy ref in sync so old code paths don’t reset pitch to 1.0
    try { rbPitchRef.current = scale; } catch {}
    // optional: keep crash guards happy
    try { (globalThis as any).__rbLastPitch = scale; } catch {}
    try { setRbPitch('*', scale); } catch {}
  }, [setRbPitch]);
  const getPitchSemitones = useCallback(() => pitchStRef.current, []);

  // Preflight reset: soft coldStart exactly once per new scope (cheap safety)
  const preflightResetIfNeeded = useCallback(async () => {
    const v = scopeVersionRef.current;
    if (lastResetForScopeRef.current === v) return; // already reset for this scope
    lastResetForScopeRef.current = v;
    try { makeTransportGate(refs).closeTransportGate(); } catch {}
    await coldStart(refs, {
      hard: false,
      bufferModeRef,
      scheduledRef,
      disposeScheduled,
      stopAll,
      pauseAll,
      setStatus,
      setCurrentTime,
    });
    // No escalation here; leave it to health probe in play flows
  }, [bufferModeRef, scheduledRef, setStatus, setCurrentTime, pauseAll]);
  try { (refs as any).preflightResetIfNeeded = preflightResetIfNeeded; } catch {}

  // Panic/Reset functionality removed — no longer exposed in UI or API

  return {
    status, duration, currentTime,
    masterVolume, setMasterVolume,
    masterFftOrder, setMasterFftOrder,
    masterFftSmoothing, setMasterFftSmoothing,
  masterPlaybackRate, setMasterPlaybackRate,
  maintainPitch, setMaintainPitch,
    masterLowpassHz, setMasterLowpassHz: setLowpass,
    masterHighpassHz, setMasterHighpassHz: setHighpass,
    volumes, muted, solo, levels,
  stemErrors,
  clearErrors: () => { try { setStemErrors(null); } catch {} },
  retryFailed: () => { try { setStemErrors(null); } catch {} try { onScopeChange(); } catch {} },
  audioRefFor, play, pause, stop: stopAndReset, seek, onScopeChange, setVolume, toggleMute, toggleSolo,
    getEffectiveGain,
    getMasterAnalyser: () => {
      // Prefer a live analyser even during muted autoplay: if the master/gate gain is ~0,
      // return the pre-fader analyser so meters/waveform still show activity.
      try {
        const gateVal = refs.gateGainRef?.current?.gain?.value ?? refs.masterGainRef?.current?.gain?.value ?? 1;
        const muted = !gateVal || gateVal <= 1e-4;
        const pre = refs.masterAnalyserPreRef?.current ?? null;
        const post = refs.masterAnalyserRef?.current ?? null;
        if (muted && pre) return pre;
        return post;
      } catch { return refs.masterAnalyserRef?.current ?? refs.masterAnalyserPreRef?.current ?? null; }
    },
    getMasterFilter: () => refs.masterFilterRef.current,
    getMasterHighpassFilter: () => refs.masterHighpassRef.current,
    getMasterLimiter: () => (refs as any).masterLimiterRef?.current ?? null,
  getMasterEnvelope: () => meters.getMasterEnvelope(),
  preflightResetIfNeeded,
    getSyncErrors,
    dsp: {
      timeStretch: timeStretchEnabled, setTimeStretch: setTimeStretchEnabled,
      eq: eqEnabled, setEq: setEqEnabled,
      eqHpf: eqHpfEnabled, setEqHpf: setEqHpfEnabled,
      eqLpf: eqLpfEnabled, setEqLpf: setEqLpfEnabled,
      q: eqQ, setQ,
      freqResponse: freqResponseEnabled, setFreqResponse: setFreqResponseEnabled,
      masterEnvelope: masterEnvelopeEnabled, setMasterEnvelope: setMasterEnvelopeEnabled,
      waveform: {
        windowMs: waveformWindowMs, setWindowMs: setWaveformWindowMs,
        fps: waveformFps, setFps: setWaveformFps,
        grid: waveformGrid, setGrid: setWaveformGrid,
        horiz: waveformHoriz, setHoriz: setWaveformHoriz,
        vert: waveformVert, setVert: setWaveformVert,
      },
  setRbTempo,
  setRbPitch,
      pitch: {
        getSemitones: getPitchSemitones,
        setSemitones: setPitchSemitones,
        getScale: () => Math.pow(2, (pitchStRef.current || 0) / 12),
      },
    },
  };

}
